import { supabase } from '@/lib/supabase';
import { offlineDb, SyncQueueItem } from './database';
import { createNotification } from '@/hooks/useNotifications';

// Only log in development mode
const isDev = import.meta.env.DEV;
const log = (...args: unknown[]) => isDev && console.log(...args);
const warn = (...args: unknown[]) => isDev && console.warn(...args);

const MAX_RETRIES = 3;
const SYNC_INTERVAL = 30000; // 30 seconds
const CONNECTIVITY_CHECK_INTERVAL = 10000; // 10 seconds
const OFFLINE_ALERT_DELAY = 5 * 60 * 1000; // 5 minutes

export interface SyncEngineState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  failedCount: number;
  lastSyncAt: Date | null;
}

type SyncStateListener = (state: SyncEngineState) => void;

class SyncEngine {
  private _isOnline = navigator.onLine;
  private _isSyncing = false;
  private _pendingCount = 0;
  private _failedCount = 0;
  private _lastSyncAt: Date | null = null;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private connectivityInterval: ReturnType<typeof setInterval> | null = null;
  private offlineTimer: ReturnType<typeof setTimeout> | null = null;
  private wentOfflineAt: Date | null = null;
  private listeners: Set<SyncStateListener> = new Set();
  private currentLocationId: string | null = null;
  private currentUserId: string | null = null;
  private hasActiveSession = false;

  constructor() {
    // Listen to browser online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  get state(): SyncEngineState {
    return {
      isOnline: this._isOnline,
      isSyncing: this._isSyncing,
      pendingCount: this._pendingCount,
      failedCount: this._failedCount,
      lastSyncAt: this._lastSyncAt,
    };
  }

  subscribe(listener: SyncStateListener): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const state = this.state;
    this.listeners.forEach((listener) => listener(state));
  }

  private handleOnline = async (): Promise<void> => {
    log('[Sync] Browser came online');
    this._isOnline = true;

    // Clear offline timer
    if (this.offlineTimer) {
      clearTimeout(this.offlineTimer);
      this.offlineTimer = null;
    }
    this.wentOfflineAt = null;

    this.notifyListeners();

    // Auto-sync when coming back online
    await this.syncNow();
  };

  private handleOffline = async (): Promise<void> => {
    log('[Sync] Browser went offline');
    this._isOnline = false;
    this.wentOfflineAt = new Date();

    this.notifyListeners();

    // Start offline alert timer if there's an active session
    if (this.hasActiveSession && this.currentLocationId) {
      this.offlineTimer = setTimeout(() => {
        this.sendOfflineAlert();
      }, OFFLINE_ALERT_DELAY);
    }
  };

  private async sendOfflineAlert(): Promise<void> {
    if (!this.currentLocationId || !this.currentUserId) return;

    try {
      // Get pending orders count and total
      const pendingItems = await offlineDb.getPendingItems();
      const orderItems = pendingItems.filter(
        (item) => item.table === 'orders' && item.action === 'create'
      );

      let unsyncedTotal = 0;
      for (const item of orderItems) {
        try {
          const data = JSON.parse(item.data);
          unsyncedTotal += data.total || 0;
        } catch {
          // Ignore parse errors
        }
      }

      // Get device info
      const deviceName = navigator.userAgent.includes('iPad')
        ? 'iPad'
        : navigator.userAgent.includes('iPhone')
        ? 'iPhone'
        : 'Web Browser';

      // This will be implemented when we add the notification type
      await createNotification({
        type: 'terminal_offline_alert',
        title: 'Terminal Offline During Active Session',
        message: `${deviceName} went offline with ${orderItems.length} pending orders ($${unsyncedTotal.toFixed(2)})`,
        locationId: this.currentLocationId,
        data: {
          device_name: deviceName,
          user_id: this.currentUserId,
          last_seen_at: this.wentOfflineAt?.toISOString() || new Date().toISOString(),
          unsynced_orders_count: orderItems.length,
          unsynced_total: unsyncedTotal,
        },
      });

      log('[Sync] Sent offline alert notification');
    } catch (error) {
      console.error('[Sync] Failed to send offline alert:', error);
    }
  }

  async start(locationId: string, userId: string): Promise<void> {
    this.currentLocationId = locationId;
    this.currentUserId = userId;

    // Load last sync time
    this._lastSyncAt = await offlineDb.getLastSyncAt();
    this._pendingCount = await offlineDb.getPendingCount();
    this._failedCount = await offlineDb.getFailedCount();
    this.notifyListeners();

    // Start periodic connectivity check (backup for browser events)
    this.connectivityInterval = setInterval(async () => {
      const wasOnline = this._isOnline;
      this._isOnline = navigator.onLine;

      if (wasOnline !== this._isOnline) {
        if (this._isOnline) {
          await this.handleOnline();
        } else {
          await this.handleOffline();
        }
      }
    }, CONNECTIVITY_CHECK_INTERVAL);

    // Start periodic sync
    this.syncInterval = setInterval(async () => {
      if (this._isOnline && !this._isSyncing) {
        await this.syncNow();
      }
    }, SYNC_INTERVAL);

    // Initial sync
    if (this._isOnline) {
      await this.syncNow();
    }

    log('[Sync] Engine started');
  }

  stop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    if (this.connectivityInterval) {
      clearInterval(this.connectivityInterval);
      this.connectivityInterval = null;
    }
    if (this.offlineTimer) {
      clearTimeout(this.offlineTimer);
      this.offlineTimer = null;
    }

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    log('[Sync] Engine stopped');
  }

  setActiveSession(active: boolean): void {
    this.hasActiveSession = active;
  }

  async syncNow(): Promise<{ success: boolean; synced: number; errors: number }> {
    if (!this._isOnline || this._isSyncing) {
      return { success: false, synced: 0, errors: 0 };
    }

    this._isSyncing = true;
    this.notifyListeners();

    let synced = 0;
    let errors = 0;

    try {
      const pendingItems = await offlineDb.getPendingItems();
      log(`[Sync] Processing ${pendingItems.length} pending items`);

      for (const item of pendingItems) {
        // Already exhausted retries on a previous run: dead-letter it so it stops
        // inflating the pending count and stops being retried automatically.
        if (item.retries >= MAX_RETRIES) {
          warn(`[Sync] Max retries exceeded for item ${item.id}; marking failed`);
          await offlineDb.markFailed(item.id, item.error_message || 'Max retries exceeded');
          errors++;
          continue;
        }

        try {
          await this.processSyncItem(item);
          await offlineDb.removeSyncItem(item.id);
          synced++;
          log(`[Sync] Synced item ${item.id}`);
        } catch (error: unknown) {
          const message = error instanceof Error
            ? error.message
            : (error && typeof error === 'object' && 'message' in error)
              ? String((error as { message: unknown }).message)
              : JSON.stringify(error) ?? 'Unknown error';
          console.error(`[Sync] Failed to sync item ${item.id}:`, message);

          // If the table/relation doesn't exist, remove the item permanently - retrying won't help
          const isFatalError = message.includes('does not exist') || message.includes('404');
          if (isFatalError) {
            warn(`[Sync] Permanently removing item ${item.id} due to unrecoverable error: ${message}`);
            await offlineDb.removeSyncItem(item.id);
          } else if (item.retries + 1 >= MAX_RETRIES) {
            // This was the final attempt - dead-letter it instead of leaving it
            // stuck in the pending count forever.
            await offlineDb.markFailed(item.id, message);
          } else {
            await offlineDb.updateSyncStatus(item.id, 'error', message);
          }
          errors++;
        }
      }

      // Refresh cache from server
      await this.refreshCache();

      this._lastSyncAt = new Date();
      await offlineDb.setLastSyncAt(this._lastSyncAt);

      log('[Sync] Sync completed');
      return { success: true, synced, errors };
    } catch (error) {
      console.error('[Sync] Sync failed:', error);
      return { success: false, synced, errors };
    } finally {
      this._pendingCount = await offlineDb.getPendingCount();
      this._failedCount = await offlineDb.getFailedCount();
      this._isSyncing = false;
      this.notifyListeners();
    }
  }

  // Requeue all dead-lettered items and immediately attempt a sync.
  async retryFailed(): Promise<{ success: boolean; synced: number; errors: number }> {
    await offlineDb.retryFailedItems();
    this._pendingCount = await offlineDb.getPendingCount();
    this._failedCount = await offlineDb.getFailedCount();
    this.notifyListeners();
    return this.syncNow();
  }

  // Permanently discard all dead-lettered items.
  async discardFailed(): Promise<void> {
    await offlineDb.discardFailedItems();
    this._pendingCount = await offlineDb.getPendingCount();
    this._failedCount = await offlineDb.getFailedCount();
    this.notifyListeners();
  }

  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    const { action, table, data: dataStr } = item;
    const data = JSON.parse(dataStr);

    switch (action) {
      case 'create': {
        const { error } = await supabase.from(table).upsert(data, { onConflict: 'id', ignoreDuplicates: true });
        if (error) throw error;
        break;
      }
      case 'update': {
        if (!data.id) throw new Error('Update requires id');
        const { id, ...updateData } = data;
        const { error } = await supabase.from(table).update(updateData).eq('id', id);
        if (error) throw error;
        break;
      }
      case 'delete': {
        if (!data.id) throw new Error('Delete requires id');
        const { error } = await supabase.from(table).delete().eq('id', data.id);
        if (error) throw error;
        break;
      }
    }
  }

  private async refreshCache(): Promise<void> {
    if (!this.currentLocationId) return;

    try {
      // Fetch and cache products
      const { data: products } = await supabase
        .from('products')
        .select('*')
        .eq('location_id', this.currentLocationId)
        .eq('is_active', true);

      if (products) {
        await offlineDb.cacheProducts(products);
      }

      // Fetch and cache categories
      const { data: categories } = await supabase
        .from('categories')
        .select('*')
        .eq('location_id', this.currentLocationId)
        .eq('is_active', true);

      if (categories) {
        await offlineDb.cacheCategories(categories);
      }

      // Fetch and cache recent orders (last 500)
      const { data: orders } = await supabase
        .from('orders')
        .select('*, items:order_items(*)')
        .eq('location_id', this.currentLocationId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (orders) {
        await offlineDb.cacheOrders(orders);
      }

      log('[Sync] Cache refreshed from server');
    } catch (error) {
      console.error('[Sync] Failed to refresh cache:', error);
    }
  }

  async testConnection(): Promise<{ success: boolean; latency: number }> {
    const start = performance.now();
    try {
      const { error } = await supabase.from('locations').select('id').limit(1);
      const latency = Math.round(performance.now() - start);
      return { success: !error, latency };
    } catch {
      return { success: false, latency: 0 };
    }
  }

  async resetLocalData(): Promise<void> {
    await offlineDb.clearCache();
    await offlineDb.clearSyncQueue();
    this._pendingCount = 0;
    this._failedCount = 0;
    this._lastSyncAt = null;
    this.notifyListeners();

    // Re-sync from server
    if (this._isOnline) {
      await this.syncNow();
    }
  }
}

// Singleton instance
export const syncEngine = new SyncEngine();
